import { toast } from '@tamagui/toast/v2';
import { Stack } from 'expo-router';
import Head from 'expo-router/head';
import { useState } from 'react';
import { YStack } from 'tamagui';

import PSButton from '@/components/ui/PSButton';
import PSInput from '@/components/ui/PSInput';
import PSText from '@/components/ui/PSText';
import { asApiError } from '@/src/api/apiError';
import authApi from '@/src/api/authApi';
import { endSession } from '@/src/lib/sessionStore';
import { PSHeaderBackButtonOnTransparentHeader } from '@/components/PSHeaderButtons';

const CONFIRM_PHRASE = 'Delete my account';

/**
 * The last step: type the phrase, then `DELETE /me`.
 *
 * The typing gate is the whole point of the screen — an account is one tap from
 * gone, and a button you can hit by accident isn't enough in front of that.
 *
 * Nothing navigates on success. This group sits inside `<Stack.Protected>`, so
 * `endSession` dropping the user is what dismisses the flow; a `router` call
 * here would race it. No farewell toast either: the flow's own toast host goes
 * with it, and the root one isn't mounted yet at that instant.
 */
export default function DeleteAccountConfirm() {
  const [phrase, setPhrase] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Case-insensitive: capitalisation isn't the deliberateness we're checking for.
  const canDelete =
    phrase.trim().toLowerCase() === CONFIRM_PHRASE.toLowerCase();

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await authApi.deleteMe();
      // The server has already killed every token on the account, so the one in
      // AsyncStorage is dead either way — this drops it and the in-memory user.
      await endSession();
    } catch (caught) {
      toast.error(asApiError(caught).message);
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Head>
        <title>Confirm deletion · Putting Scene</title>
      </Head>
      <Stack.Screen
        options={{
          headerLeft: () => <PSHeaderBackButtonOnTransparentHeader />,
          unstable_headerLeftItems: () => [
            {
              type: 'custom',
              element: <PSHeaderBackButtonOnTransparentHeader />,
              hidesSharedBackground: true,
            },
          ],
        }}
      />
      <YStack px="$lg" pt="$xl" gap="$lg">
        <YStack gap="$sm">
          <PSText.Title>Are you sure?</PSText.Title>
          <PSText.Body2 color="$textDarkMuted">
            This deletes your account immediately and permanently. Type “
            {CONFIRM_PHRASE}” below to confirm.
          </PSText.Body2>
        </YStack>

        <PSInput
          value={phrase}
          onChangeText={setPhrase}
          placeholder={CONFIRM_PHRASE}
          autoFocus
          disabled={isDeleting}
        />

        <PSButton
          variant="destructive"
          title="Delete my account"
          loading={isDeleting}
          loadingTitle="Deleting"
          disabled={!canDelete}
          onPress={handleDelete}
        />
      </YStack>
    </>
  );
}
